import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ArrowRight, Target, Brain, CheckCircle, Users, Shield, TrendingUp, GraduationCap, Scale, BarChart3 } from 'lucide-react';

const impactASPlusLogo = "/opt/brand/impactas-final-2.webp";
const jobFitLogo = "/opt/photo/jobfit.webp";
const insightEdgeLogo = "/opt/photo/insightedge.webp";

const poorSelectionRisks = [
  "Misaligned hires",
  "Slower time to productivity",
  "Higher attrition",
  "Weak leadership pipelines over time",
  "Poor succession planning"
];

const jobFitClarifies = [
  "What success looks like in specific early-career roles",
  "Behavioural and capability expectations",
  "Alignment between individual strengths and role demands"
];

const jobFitSupports = [
  "Graduate recruitment programmes",
  "Assessment centres",
  "Entry-level and early-career hiring",
  "Large-scale recruitment campaigns"
];

const insightEdgeProvides = [
  "Cognitive reasoning and learning agility",
  "Decision-making approach",
  "Ability to process complexity",
  "Potential to grow into more demanding roles over time"
];

const scaleAndFairness = [
  "Assess large candidate volumes consistently",
  "Reduce bias through structured insight",
  "Improve quality of hire",
  "Strengthen long-term talent pipelines"
];

const pipelineSupports = [
  "Ongoing development",
  "Leadership readiness assessment",
  "Coaching and capability building",
  "Succession and leadership insight"
];

const EarlyCareerSelectionPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Graduate Assessment Centre Nigeria | Graduate Recruitment Lagos"
        description="Graduate assessment centres and career selection in Nigeria. Design and delivery of graduate recruitment programmes, aptitude testing, and assessment centres for Nigerian employers. Fair, data-driven selection at scale."
        keywords="graduate assessment centre Nigeria, graduate recruitment Lagos, assessment centre design Nigeria, aptitude test Nigeria, graduate hiring Lagos, early career recruitment Nigeria, campus recruitment Africa, graduate scheme Nigeria, trainee selection Lagos"
        canonical="/early-career-selection"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section 
          className="relative min-h-[70vh] lg:min-h-[75vh] flex items-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `linear-gradient(to right, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.8) 40%, rgba(10, 10, 10, 0.4) 100%), url('/opt/photo/ext/p3184292.webp')`
          }}
        >
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8 pt-24">
            <div className="max-w-3xl">
              {/* Impact AS Plus Logo */}
              <img 
                src={impactASPlusLogo} 
                alt="Impact AS Plus" 
                className="h-12 lg:h-14 w-auto mb-8"
              />
              
              <h1 className="text-white font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                Career Selection & Assessment Centres
              </h1>
              
              <p className="text-[#2693d0] text-lg lg:text-xl xl:text-2xl font-medium leading-relaxed mb-8">
                Evidence-based insight for graduate recruitment and early-career assessment, powered by Impact AS Plus™
              </p>
            </div>
          </div>
        </section>

        {/* Opening Copy Section */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <p className="text-slate-700 text-lg lg:text-xl leading-relaxed mb-6">
                Early-career recruitment decisions shape the future leadership pipeline of an organisation.
              </p>
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                Graduate recruitment and assessment centres often focus on academic achievement and surface competencies, yet struggle to assess how individuals will perform in complex, fast-paced organisational environments.
              </p>
              <div className="bg-[#f0f5f8] rounded-xl p-6 lg:p-8 border border-slate-100">
                <p className="text-slate-800 font-medium text-lg lg:text-xl leading-relaxed">
                  <span className="text-[#2693d0]">Impact AS Plus™</span> supports organisations to design and deliver robust, evidence-based graduate recruitment and assessment centre processes, providing deeper insight into capability, reasoning, and role fit at scale.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Getting Early Career Decisions Right */}
        <section className="bg-[#1e3a5f] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl">
              <h2 className="text-white font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Getting Early Career Decisions Right
              </h2>
              
              <p className="text-slate-300 text-lg lg:text-xl leading-relaxed mb-8">
                Early career appointments are <span className="text-[#2693d0] font-medium">high-volume, high-impact decisions</span>.
              </p>
              
              <p className="text-slate-300 text-base lg:text-lg leading-relaxed mb-6">
                When selection is poorly designed, organisations face:
              </p>
              
              <ul className="space-y-4 mb-10">
                {poorSelectionRisks.map((risk, index) => (
                  <li key={index} className="flex items-start gap-4">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-slate-200 text-base lg:text-lg leading-relaxed">{risk}</span>
                  </li>
                ))}
              </ul>
              
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 lg:p-8">
                <p className="text-white font-medium text-lg lg:text-xl leading-relaxed">
                  Impact AS Plus™ enables organisations to move beyond CVs and interviews, towards structured insight that predicts performance and potential.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Impact AS Plus Diagnostics for Assessment Centres */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
              Impact AS Plus™ Diagnostics for Assessment Centres
            </h2>
            
            {/* Framing line */}
            <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-16 max-w-3xl">
              Detailed product information is available within the Impact AS Plus™ environment, where diagnostics are positioned within a broader leadership and talent system.
            </p>
            
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
              {/* JobFit™ */}
              <div className="bg-[#f0f5f8] rounded-2xl p-8 lg:p-10 border border-slate-100 flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                      <Target className="w-6 h-6 text-[#2693d0]" />
                    </div>
                    <h3 className="text-slate-800 font-semibold text-xl lg:text-2xl">JobFit™</h3>
                  </div>
                  <img 
                    src={jobFitLogo} 
                    alt="JobFit logo" 
                    className="h-10 lg:h-12 w-auto object-contain"
                  />
                </div>
                
                <p className="text-slate-700 text-base lg:text-lg leading-relaxed mb-6">
                  JobFit™ is a role-based diagnostic designed for graduate recruitment and early-career assessment.
                </p>
                
                <p className="text-slate-600 text-base leading-relaxed mb-4 font-medium">It helps organisations clarify:</p>
                <ul className="space-y-3 mb-8">
                  {jobFitClarifies.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-600 text-base leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <p className="text-slate-600 text-base leading-relaxed mb-4 font-medium">JobFit™ is commonly used to support:</p>
                <ul className="space-y-3 mb-8">
                  {jobFitSupports.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-[#2693d0] flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600 text-base leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                
                {/* Button CTA - pushed to bottom */}
                <div className="mt-auto pt-6 border-t border-slate-200">
                  <a
                    href="/product/jobfit"
                    className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-5 py-3 rounded font-medium text-sm hover:bg-[#1e7ab8] transition-all duration-300 w-full sm:w-auto"
                  >
                    Explore JobFit™
                    <ArrowRight size={16} />
                  </a>
                  <p className="text-slate-500 text-sm mt-4">
                    JobFit™ is part of the Impact AS Plus™ leadership and talent intelligence system.
                  </p>
                </div>
              </div>
              
              {/* InsightEdge™ */}
              <div className="bg-[#f0f5f8] rounded-2xl p-8 lg:p-10 border border-slate-100 flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#2693d0]/10 flex items-center justify-center">
                      <Brain className="w-6 h-6 text-[#2693d0]" />
                    </div>
                    <h3 className="text-slate-800 font-semibold text-xl lg:text-2xl">InsightEdge™</h3>
                  </div>
                  <img 
                    src={insightEdgeLogo} 
                    alt="InsightEdge logo" 
                    className="h-10 lg:h-12 w-auto object-contain"
                  />
                </div>
                
                <p className="text-slate-700 text-base lg:text-lg leading-relaxed mb-6">
                  InsightEdge™ assesses how individuals think, reason, and approach problem-solving.
                </p>
                
                <p className="text-slate-600 text-base leading-relaxed mb-4 font-medium">It provides insight into:</p>
                <ul className="space-y-3 mb-8">
                  {insightEdgeProvides.map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                      <span className="text-slate-600 text-base leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="bg-white rounded-lg p-4 border border-slate-200 mb-8">
                  <p className="text-slate-700 text-sm lg:text-base leading-relaxed">
                    InsightEdge™ is particularly valuable in assessment centres where <span className="font-medium">future potential, not just current competence</span>, matters.
                  </p>
                </div>
                
                {/* Button CTA - pushed to bottom */}
                <div className="mt-auto pt-6 border-t border-slate-200">
                  <a
                    href="/product/insightedge"
                    className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-5 py-3 rounded font-medium text-sm hover:bg-[#1e7ab8] transition-all duration-300 w-full sm:w-auto"
                  >
                    Explore InsightEdge™
                    <ArrowRight size={16} />
                  </a>
                  <p className="text-slate-500 text-sm mt-4">
                    InsightEdge™ is delivered as part of Impact AS Plus™ and used within structured assessment centre designs.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Built for Volume, Consistency, and Fairness */}
        <section className="relative bg-[#f0f5f8] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-4xl">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-8 leading-tight">
                Built for Volume, Consistency, and Fairness
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-8">
                Delivered through Impact AS Plus™, JobFit™ and InsightEdge™ allow organisations to:
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4 mb-10">
                {scaleAndFairness.map((item, index) => (
                  <div key={index} className="flex items-center gap-4 bg-white rounded-lg p-5 border border-slate-200">
                    <CheckCircle className="w-5 h-5 text-[#2693d0] flex-shrink-0" />
                    <span className="text-slate-700 text-base leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>
              
              <div className="bg-white rounded-xl p-6 lg:p-8 card-pulse-border">
                <p className="text-slate-700 text-base lg:text-lg leading-relaxed">
                  All assessments are supported by <span className="font-medium">secure data handling</span>, <span className="font-medium">consistent reporting</span>, and <span className="font-medium">enterprise-ready governance</span>.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* From Graduate Intake to Future Leaders */}
        <section className="bg-white py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div>
                <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                  From Graduate Intake to Future Leaders
                </h2>
                
                <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-6">
                  While JobFit™ and InsightEdge™ are designed for early-career recruitment, they form the foundation of a broader talent and leadership ecosystem.
                </p>
                
                <p className="text-slate-700 text-base lg:text-lg leading-relaxed mb-6">
                  As individuals progress, Impact AS Plus™ supports:
                </p>
                
                <ul className="space-y-4 mb-8">
                  {pipelineSupports.map((item, index) => (
                    <li key={index} className="flex items-center gap-4">
                      <TrendingUp className="w-5 h-5 text-[#2693d0] flex-shrink-0" />
                      <span className="text-slate-700 text-base lg:text-lg leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                
                <a
                  href="/leadership-succession-intelligence"
                  className="inline-flex items-center gap-2 text-[#2693d0] font-medium hover:underline"
                >
                  Explore Leadership & Succession Intelligence
                  <ArrowRight size={16} />
                </a>
              </div>
              
              <div className="bg-[#1e3a5f] rounded-2xl p-8 lg:p-10">
                <GraduationCap className="w-12 h-12 text-[#2693d0] mb-6" />
                <p className="text-white font-medium text-lg lg:text-xl leading-relaxed">
                  This enables organisations to track talent from entry point through to leadership maturity.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-[#e8f2f7] py-20 lg:py-28">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-slate-800 font-semibold text-2xl sm:text-3xl lg:text-4xl mb-6 leading-tight">
                Explore Further
              </h2>
              
              <p className="text-slate-600 text-base lg:text-lg leading-relaxed mb-10">
                Ready to strengthen your graduate recruitment and assessment centre programmes?
              </p>
              
              <a
                href="/briefing"
                className="inline-flex items-center justify-center gap-2 bg-[#2693d0] text-white px-8 py-4 rounded font-medium text-base hover:bg-[#1e7ab8] transition-all duration-300"
              >
                Book a leadership briefing
                <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer hidePreFooterCta={true} />
    </div>
  );
};

export default EarlyCareerSelectionPage;
